import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook to manage notification sound playback
 * Reads preferences from localStorage and provides a function to play notification sounds
 */
const useNotificationSound = () => {
  const audioContextRef = useRef(null);

  // Initialize and reuse a single AudioContext
  useEffect(() => {
    // Create AudioContext lazily on first use to avoid autoplay restrictions
    return () => {
      // Clean up AudioContext on unmount
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      // Get settings from localStorage
      const settings = localStorage.getItem('loadboard:notificationSettings');
      const notificationSettings = settings ? JSON.parse(settings) : {};

      const soundEnabled = notificationSettings.soundEnabled ?? false;

      if (!soundEnabled) {
        return;
      }

      // Create or reuse AudioContext (prevents "AudioContext was not allowed to start" errors)
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const audioContext = audioContextRef.current;

      // Resume context if suspended (required by some browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Soft bell-like tone (E6 note at 1318.51 Hz)
      oscillator.frequency.setValueAtTime(1318.51, audioContext.currentTime);
      oscillator.type = 'sine'; // Smooth sine wave for soft tone

      // Volume envelope - soft attack and quick fade (fixed at comfortable level)
      const volume = 0.12; // Fixed comfortable volume
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.02); // Quick attack
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4); // Gentle fade

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4); // Stop after 0.4 seconds

      // No need to close AudioContext - we reuse it for better performance
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }, []);

  return { playNotificationSound };
};

export default useNotificationSound;
